import { LaunchDarklyFeatureFlags } from '@core/feature-flags';
import { useLicense } from '@core/license';
import { useUser } from '@core/user';
import { useCallback } from 'react';
/**
 * Captures the current session context (user, license, feature flags) and returns a stable
 * {@link AuthorizationResolver} that can evaluate any number of predicates against it.
 *
 * Use when several predicates must be evaluated in the same render (e.g. filtering columns
 * by a per-item `canRender`), where calling {@link useIsAuthorized} in a loop would violate
 * the rules of hooks. For a single check, prefer {@link useIsAuthorized}; for declarative
 * JSX gating, prefer `<Guard>`.
 *
 * The returned function is memoized against `user` and `license`, safe to use as a dependency.
 *
 * @example
 * ```tsx
 * const resolve = useAuthorizationResolver();
 * const visibleColumns = useMemo(
 *     () => columns.filter((column) => resolve(column.meta?.canRender ?? true)),
 *     [columns, resolve],
 * );
 * ```
 */ export var useAuthorizationResolver = function useAuthorizationResolver() {
    var _useUser = useUser(), user = _useUser.access;
    var license = useLicense();
    return useCallback(function(canRender) {
        return typeof canRender === 'boolean' ? canRender : canRender({
            user: user,
            license: license,
            hasFeatureFlag: function hasFeatureFlag(flag, project) {
                return LaunchDarklyFeatureFlags.isActive(flag, project);
            }
        });
    }, [
        user,
        license
    ]);
};

//# sourceMappingURL=useAuthorizationResolver.js.map